import React, { useEffect, useState, useCallback } from "react";
import { Search, SlidersHorizontal, Loader2, Star, UserCheck, Send } from "lucide-react";
import { Layout } from "@/components/Layout";
import { CandidateCard } from "@/components/CandidateCard";
import { api, useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function FindPeople() {
  const { t } = useLang();
  const { user } = useAuth();
  const [filters, setFilters] = useState({ q: "", city: "", availability: "", work_type: "" });
  const [data, setData] = useState({ results: [], total: 0, sponsored_count: 0 });
  const [busy, setBusy] = useState(true);

  const run = useCallback(async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/candidates/search", { params: clean });
      setData(data);
    } catch { /* ignore */ } finally { setBusy(false); }
  }, [filters]);

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const act = async (endpoint, candidate_id, label) => {
    if (!user || user.role !== "employer") { toast.error("Log in as an employer to do that"); return; }
    try { await api.post(endpoint, { candidate_id }); toast.success(label); }
    catch (e) { toast.error("Action failed"); }
  };

  const empActions = (c) => user?.role === "employer" ? (
    <div className="flex gap-1.5">
      <button data-testid={`shortlist-${c.id}`} onClick={() => act("/employer/shortlist", c.id, "Shortlisted")} title={t("shortlist")}
        className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-navy hover:bg-brand-bluelight"><Star className="h-4 w-4" /></button>
      <button data-testid={`invite-${c.id}`} onClick={() => act("/employer/invite", c.id, "Invited to apply")} title={t("invite")}
        className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-navy hover:bg-brand-bluelight"><Send className="h-4 w-4" /></button>
    </div>
  ) : null;

  return (
    <Layout>
      <div className="bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="font-display text-3xl font-extrabold text-white">Who are you looking for?</h1>
          <p className="mt-1 text-sm text-blue-200">Search the global workforce — find people available now.</p>
          <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-2 rounded-xl bg-white px-4 py-3">
              <Search className="h-5 w-5 text-slate-400" />
              <input data-testid="people-search-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="e.g. Sales, Designer, Developer" className="w-full text-sm focus:outline-none" />
            </div>
            <input data-testid="people-search-city" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              placeholder={t("city_placeholder")} className="rounded-xl bg-white px-4 py-3 text-sm focus:outline-none sm:w-48" />
            <button type="submit" data-testid="people-search-btn" className="gjt-btn-orange rounded-xl px-8 py-3 text-sm font-bold">{t("search")}</button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center gap-3">
          <SlidersHorizontal className="h-4 w-4 text-slate-400" />
          <select data-testid="people-filter-availability" value={filters.availability}
            onChange={(e) => setFilters({ ...filters, availability: e.target.value })}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none">
            <option value="">Any availability</option>
            <option value="now">Available now</option>
            <option value="week">Available this week</option>
            <option value="date">Available from date</option>
          </select>
          <select data-testid="people-filter-worktype" value={filters.work_type}
            onChange={(e) => setFilters({ ...filters, work_type: e.target.value })}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy capitalize focus:outline-none">
            {["", "full-time", "part-time", "freelance", "gig", "remote", "hybrid", "on-site", "contract"].map((o) => <option key={o} value={o}>{o || "Any work type"}</option>)}
          </select>
          <button onClick={run} className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white">Apply</button>
          <span className="ml-auto text-sm text-slate-500">{data.total} people{data.sponsored_count ? ` · ${data.sponsored_count} sponsored` : ""}</span>
        </div>

        {busy ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.results.map((c, i) => <CandidateCard key={c.id} c={c} index={i} actions={empActions(c)} />)}
          </div>
        )}
        {!busy && data.results.length === 0 && <p className="py-20 text-center text-slate-400">No candidates found.</p>}
      </div>
    </Layout>
  );
}
